import React, { useState, useEffect } from 'react';
import { Card, Text } from '@tremor/react';
import { supabase } from '../../lib/supabase';
import { getAvatarUrl } from '../../utils/avatarUtils';
import './RecentActivity.css';

/** Spójnie z resztą appki (SingleEvent / users), nie „profiles” */
function userDisplayName(user) {
  if (!user) return 'Unknown';
  if (user.nickname) return user.nickname;
  if (user.first_name && user.last_name) return `${user.first_name} ${user.last_name}`.trim();
  if (user.name) return user.name;
  return 'Unknown';
}

function RecentActivity() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRecentActivity = async () => {
      try {
        // Pobierz ostatnie wydarzenia
        const { data: events, error: eventsError } = await supabase
          .from('events')
          .select('id, title, created_at, host_id')
          .order('created_at', { ascending: false })
          .limit(5);

        if (eventsError) throw eventsError;

        // Pobierz hostów dla wydarzeń
        const hostIds = [...new Set((events || []).map(e => e.host_id).filter(Boolean))];
        const { data: hosts, error: hostsError } = hostIds.length > 0
          ? await supabase
              .from('users')
              .select('id, name, nickname, first_name, last_name, avatar_url')
              .in('id', hostIds)
          : { data: [], error: null };

        if (hostsError) throw hostsError;

        const hostsMap = new Map((hosts || []).map(h => [h.id, h]));

        // Pobierz ostatnie uczestnictwa
        const { data: participations, error: partError } = await supabase
          .from('event_participants')
          .select('event_id, user_id, joined_at')
          .order('joined_at', { ascending: false })
          .limit(5);

        if (partError) throw partError;

        // Pobierz tytuły wydarzeń dla uczestnictw
        const eventIds = [...new Set((participations || []).map(p => p.event_id).filter(Boolean))];
        const { data: eventTitles, error: eventsTitleError } = eventIds.length > 0
          ? await supabase
              .from('events')
              .select('id, title')
              .in('id', eventIds)
          : { data: [], error: null };

        if (eventsTitleError) throw eventsTitleError;

        const eventsMap = new Map((eventTitles || []).map(e => [e.id, e]));

        // Pobierz użytkowników dla uczestnictw
        const userIds = [...new Set((participations || []).map(p => p.user_id).filter(Boolean))];
        const { data: users, error: usersError } = userIds.length > 0
          ? await supabase
              .from('users')
              .select('id, name, nickname, first_name, last_name, avatar_url')
              .in('id', userIds)
          : { data: [], error: null };

        if (usersError) throw usersError;

        const usersMap = new Map((users || []).map(u => [u.id, u]));

        // Połącz i posortuj aktywności
        const allActivities = [
          ...(events || []).map(event => {
            const host = hostsMap.get(event.host_id);
            return {
              type: 'created',
              eventTitle: event.title,
              username: userDisplayName(host),
              avatar: host?.avatar_url,
              timestamp: event.created_at,
            };
          }),
          ...(participations || []).map(part => {
            const event = eventsMap.get(part.event_id);
            const user = usersMap.get(part.user_id);
            return {
              type: 'joined',
              eventTitle: event?.title || 'Event',
              username: userDisplayName(user),
              avatar: user?.avatar_url,
              timestamp: part.joined_at,
            };
          }),
        ]
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          .slice(0, 3);

        setActivities(allActivities);
      } catch {
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };

    loadRecentActivity();
  }, []);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Card className="recent-activity-widget">
        <Text className="widget-title">Recent Activity</Text>
        <div className="recent-activity-loading">
          <Text>Loading...</Text>
        </div>
      </Card>
    );
  }

  if (activities.length === 0) {
    return (
      <Card className="recent-activity-widget">
        <Text className="widget-title">Recent Activity</Text>
        <Text className="recent-activity-empty">No recent activity</Text>
      </Card>
    );
  }

  return (
    <Card className="recent-activity-widget">
      <Text className="widget-title">Recent Activity</Text>
      <div className="recent-activity-list">
        {activities.map((activity, index) => (
          <div key={index} className="activity-item">
            <div className="activity-avatar">
              {activity.avatar ? (
                <img src={getAvatarUrl(activity.avatar)} alt={activity.username} />
              ) : (
                <div className="activity-avatar-placeholder">
                  {activity.username.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="activity-content">
              <Text className="activity-text">
                <strong>{activity.username}</strong>{' '}
                {activity.type === 'created' ? 'created' : 'joined'}{' '}
                <strong>{activity.eventTitle}</strong>
              </Text>
              <Text className="activity-time">{formatTime(activity.timestamp)}</Text>
            </div>
            <span className="activity-arrow">→</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default RecentActivity;
